import { useEffect, useMemo } from "react";
import { useMerchantDomainKnowledge, useMerchantRiskCatalog, useMerchantRisksList } from "@/api/merchant-risks";
import { SAFETY_POLICY_LABELS, useSafetyPolicies } from "@/api/safety-policies";
import { buildKeywordGroups } from "@/components/safety-config/keyword-config";
import { RiskGroupAccordion } from "@/components/safety-config/RiskGroupAccordion";
import type { RiskState } from "@/components/safety-config/RiskRow";
import type { RiskItem, Severity } from "@/components/safety-config/risk-groups";
import { buildRiskGroupsFromCatalog, findGroupIdForSelection } from "@/components/safety-config/risk-groups";
import { warning } from "@/providers/ToastProvider";
import { RiskCtaBanner } from "./RiskCtaBanner";
import { RiskSummaryBand } from "./RiskSummaryBand";

type TabRisksProps = {
  selectedRiskIds?: string[];
  onSelectRisk: (id: string) => void;
  onRemoveRisks: (invalidIds: string[]) => void;
};

export const TabRisks = ({ selectedRiskIds, onSelectRisk, onRemoveRisks }: TabRisksProps) => {
  const { data: catalog, isLoading: catalogLoading } = useMerchantRiskCatalog();
  const { data: merchantRisksList } = useMerchantRisksList();
  const { data: safetyPolicies } = useSafetyPolicies();
  const { data: domainKnowledge } = useMerchantDomainKnowledge();

  const groups = useMemo(() => buildRiskGroupsFromCatalog(catalog ?? []), [catalog]);

  // Ids in the URL that match nothing once the catalog has loaded (stale link, deleted
  // risk, wrong merchant) are dropped from the selection instead of being left dangling.
  useEffect(() => {
    if (catalogLoading || !selectedRiskIds?.length) return;
    const invalid = selectedRiskIds.filter((id) => !findGroupIdForSelection(groups, id));
    if (invalid.length > 0) {
      onRemoveRisks(invalid);
      warning("We couldn't find that id, please try a different one");
    }
  }, [catalogLoading, groups, selectedRiskIds, onRemoveRisks]);

  // The merchant's per-risk config (enabled state, auto-confirm, and the safety
  // policy each risk is mapped to), keyed by riskId.
  const riskById = new Map((merchantRisksList ?? []).map((r) => [r.riskId, r]));
  const policyLabelById = new Map((safetyPolicies ?? []).map((p) => [p.id, SAFETY_POLICY_LABELS[p.type] ?? p.type]));

  const getRiskState = (item: RiskItem): RiskState => {
    const keys = item.keys ?? [item.key];
    // For a collapsed row, surface config from the first of its merged risks
    // that the merchant has enabled.
    const enabledRisk = keys.map((k) => riskById.get(k)).find(Boolean);

    // Effective config (merchant override over catalog default) drives the
    // read-only keyword + placeholder summary.
    const effectiveConfig = { ...item.config, ...((enabledRisk?.config as Record<string, unknown>) ?? {}) };

    return {
      enabled: keys.some((k) => riskById.has(k)),
      autoConfirm: enabledRisk?.autoConfirm ?? false,
      policy: enabledRisk?.safetyPolicyId ? (policyLabelById.get(enabledRisk.safetyPolicyId) ?? null) : null,
      severity: (item.defaultSeverity ?? "medium") as Severity,
      keywords: buildKeywordGroups(item.modelType, effectiveConfig, domainKnowledge ?? {}),
      customCreatorRiskExplanation: enabledRisk?.customCreatorRiskExplanation ?? null,
    };
  };

  return (
    <div className="flex flex-col gap-8">
      <RiskSummaryBand groups={groups} getRiskState={getRiskState} />
      <RiskGroupAccordion
        groups={groups}
        getRiskState={getRiskState}
        selectedRiskIds={selectedRiskIds}
        onSelectRisk={onSelectRisk}
        onRemoveRisks={onRemoveRisks}
      />
      <RiskCtaBanner />
    </div>
  );
};
